import React, { useState, useEffect, Component } from "react";
import DealEditor from "./DealEditor";
import type { Partner, Deal, Principal, InPartner, Country } from "../types";
import { STAGES } from "../types";
import { useData } from "../hooks/useData";
import { api } from "../api/client";
import { I } from "./Icons";
import {
  Avatar,
  clsx,
  HeatMeter,
  Metric,
  Money,
  SectionLabel,
} from "./Primitives";

class ErrorBoundary extends Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="screen pipeline" style={{ padding: 40 }}>
          <h1 className="page-title">Pipeline</h1>
          <div
            className="ai-error"
            style={{
              marginTop: 20,
              padding: 20,
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 8,
            }}
          >
            <strong>Something went wrong rendering the pipeline.</strong>
            <pre style={{ marginTop: 12, fontSize: 12, whiteSpace: "pre-wrap" }}>
              {this.state.error.message}
              {"\n"}
              {this.state.error.stack}
            </pre>
            <button
              className="btn primary sm"
              style={{ marginTop: 12 }}
              onClick={() => this.setState({ error: null })}
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

interface PipelineHubProps {
  go: (screen: string, id?: string) => void;
  openAI: (prefill?: string) => void;
  initialTab: string;
  partners: Record<string, Partner>;
}

function ProbBar({ v }: { v: number }) {
  const val = typeof v === "number" && isFinite(v) ? v : 0;
  return (
    <span className="prob-bar">
      <span className="prob-fill" style={{ width: val + "%" }} />
    </span>
  );
}

function SafeHeatMeter({ level }: { level: string | null | undefined }) {
  try {
    if (!level) return <span className="heat"><span className="heat-bar" /><span className="heat-bar" /><span className="heat-bar" /></span>;
    return <HeatMeter level={level as any} />;
  } catch {
    return null;
  }
}

function PipeRow({
  d,
  go,
  partners,
  onArchive,
  onEdit,
}: {
  d: Deal;
  go: (screen: string, id?: string) => void;
  partners: Record<string, Partner>;
  onArchive: (id: string, name: string) => void;
  onEdit: (d: Deal) => void;
}) {
  const owner = d.owner ? partners[d.owner] || null : null;
  const valueM = typeof d.value_m === "number" ? d.value_m : 0;
  const prob = typeof d.probability === "number" ? d.probability : 0;

  return (
    <div className="pt-row" onClick={() => go("project", d.id)}>
      <span className="pt-c-deal">
        <span className="ptr-name">{d.name || "Untitled deal"}</span>
        <span className="ptr-tags">{Array.isArray(d.tags) && d.tags.length > 0 ? d.tags[0] : ""}</span>
      </span>
      <span className="pt-c-rep">
        <span className="ptr-rep" style={{ color: d.principal_color || undefined }}>
          {d.principal_name || "—"}
        </span>
        {I.arrowRight({ size: 12, w: 1.7, style: { color: "var(--faint)", flex: "none" } })}
        <span className="ptr-op">{d.operator_name || "—"}</span>
      </span>
      <span className="pt-c-mkt">
        {I.globe({ size: 12, w: 1.7 })}
        {d.market || "—"}
      </span>
      <span className="pt-c-val mono">${valueM.toFixed(1)}M</span>
      <span className="pt-c-prob">
        <ProbBar v={prob} />
        <span className="ptr-prob mono">{prob}%</span>
      </span>
      <span className="pt-c-owner">
        <Avatar partner={owner} size={24} />
      </span>
      <span className="pt-c-heat">
        <SafeHeatMeter level={d.heat} />
      </span>
      <span className="pt-c-act">
        <button className="icon-btn" title="Edit deal" aria-label={`Edit ${d.name}`}
          onClick={(e) => { e.stopPropagation(); onEdit(d); }}>
          {I.sliders({ size: 15 })}
        </button>
        <button className="icon-btn ptr-del" title="Delete deal" aria-label={`Delete ${d.name}`}
          onClick={(e) => { e.stopPropagation(); onArchive(d.id, d.name || "this deal"); }}>
          {I.trash({ size: 15 })}
        </button>
      </span>
    </div>
  );
}

function Pipeline({
  go,
  openAI,
  partners,
}: {
  go: (screen: string, id?: string) => void;
  openAI: (prefill?: string) => void;
  partners: Record<string, Partner>;
}) {
  const { data: deals, loading, error, refetch } = useData<Deal[]>("/api/deals");
  const [archivedIds, setArchivedIds] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<Deal | null>(null);

  const archiveDeal = async (id: string, name: string) => {
    if (!window.confirm(`Delete "${name}" from the pipeline? (An admin can restore it from Settings.)`)) return;
    setArchivedIds(prev => new Set([...prev, id]));
    try {
      await api.delete("/api/deals/" + id);
      refetch?.();
    } catch {
      setArchivedIds(prev => { const next = new Set(prev); next.delete(id); return next; });
    }
  };

  if (error) {
    return (
      <div className="screen pipeline">
        <div className="page-head">
          <div>
            <h1 className="page-title">Pipeline</h1>
            <div className="page-sub">Could not load deals</div>
          </div>
        </div>
        <div
          className="ai-error"
          style={{
            padding: 16,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 8,
            marginTop: 12,
          }}
        >
          Error: {error}
        </div>
      </div>
    );
  }

  if (loading && !deals) {
    return (
      <div className="screen pipeline">
        <div className="page-head">
          <div>
            <h1 className="page-title">Pipeline</h1>
            <div className="page-sub">Loading deals...</div>
          </div>
        </div>
      </div>
    );
  }

  const rows = (Array.isArray(deals) ? deals : []).filter(d => !archivedIds.has(d.id));
  const totalVal = rows.reduce((a, d) => a + (typeof d.value_m === "number" ? d.value_m : 0), 0);
  const weighted = rows.reduce(
    (a, d) => a + ((typeof d.value_m === "number" ? d.value_m : 0) * (typeof d.probability === "number" ? d.probability : 0)) / 100,
    0
  );
  const marketCount = new Set(rows.map((d) => d.market).filter(Boolean)).size;

  return (
    <div className="screen pipeline">
      <div className="page-head">
        <div>
          <h1 className="page-title">Pipeline</h1>
          <div className="page-sub">
            {rows.length} deal{rows.length !== 1 ? "s" : ""} &middot; {marketCount} market{marketCount !== 1 ? "s" : ""}
          </div>
        </div>
        <div className="pipe-totals">
          <Metric k="Total pipeline" v={<Money v={totalVal} />} />
          <Metric k="Weighted" v={<Money v={weighted} />} />
          <button className="btn ghost" onClick={() => window.open("/api/reports/pipeline", "_blank")} title="Printable pipeline report">
            {I.doc({ size: 15 })}
            Report
          </button>
          <button className="btn primary" onClick={() => openAI("Add a deal: ")}>
            {I.plus({ size: 15 })}
            New deal
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div style={{ padding: "40px 0", textAlign: "center", color: "var(--muted)", fontSize: 14 }}>
          No deals in the pipeline yet. Use "Ask Saga" or the "New deal" button to add one.
        </div>
      ) : (
        <div className="pipe-table">
          <div className="pt-head">
            <span className="pt-c-deal">Deal</span>
            <span className="pt-c-rep">Principal &rarr; counterparty</span>
            <span className="pt-c-mkt">Market</span>
            <span className="pt-c-val">Value</span>
            <span className="pt-c-prob">Prob.</span>
            <span className="pt-c-owner">Owner</span>
            <span className="pt-c-heat">Heat</span>
            <span className="pt-c-act" />
          </div>
          {STAGES.map((st) => {
            const stageRows = rows.filter((d) => d.stage === st.id);
            if (stageRows.length === 0) return null;
            const sv = stageRows.reduce((a, d) => a + (typeof d.value_m === "number" ? d.value_m : 0), 0);
            return (
              <div key={st.id} className="pt-group">
                <div className="pt-group-head">
                  <span className="ptg-dot" data-stage={st.id} />
                  <span className="ptg-label">{st.label}</span>
                  <span className="ptg-count">{stageRows.length}</span>
                  <span className="ptg-val mono">${sv.toFixed(1)}M</span>
                </div>
                {stageRows.map((d) => (
                  <PipeRow key={d.id} d={d} go={go} partners={partners} onArchive={archiveDeal} onEdit={setEditing} />
                ))}
              </div>
            );
          })}
        </div>
      )}
      {editing && (
        <DealEditor deal={editing} partners={partners} onClose={() => setEditing(null)} onSaved={() => refetch?.()} />
      )}
    </div>
  );
}

const C_STATUS: Record<string, { label: string; cls: string }> = {
  current: { label: "Up to date", cls: "ok" },
  stale: { label: "Needs refresh", cls: "warn" },
  draft: { label: "Draft", cls: "draft" },
  none: { label: "Not started", cls: "none" },
};

function CountryCard({ c, onOpen }: { c: Country; onOpen: () => void }) {
  const st = C_STATUS[c.status] || C_STATUS.none;
  return (
    <div className="country-card" onClick={onOpen}>
      <div className="cc-top">
        <span className="cc-flag">{c.flag}</span>
        <div className="cc-titles">
          <div className="cc-country">{c.name}</div>
          <div className="cc-rev">{c.region}</div>
        </div>
        <span className={clsx("c-status", st.cls)}>{st.label}</span>
      </div>
      <div className="cc-headline">{c.tagline}</div>
      <div className="cc-foot">
        <span className="cc-sent">{c.info_url ? "info.saga-advisory.com" : `Updated ${c.updated_date || "—"}`}</span>
        <span className="cc-open">
          Open{I.arrowRight({ size: 13 })}
        </span>
      </div>
    </div>
  );
}

function CountryReader({
  c,
  close,
  openAI,
}: {
  c: Country;
  close: () => void;
  go: (screen: string, id?: string) => void;
  openAI: (prefill?: string) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const sections: { h: string; b: string }[] = [
    { h: "Overview", b: c.overview },
    { h: "Key Developments", b: c.key_developments },
    { h: "Opportunities", b: c.opportunities },
    { h: "Risks", b: c.risks },
    { h: "Saga Position", b: c.saga_position },
  ].filter((s) => s.b);

  const refresh = async () => {
    setLoading(true);
    setDraft(null);
    try {
      const ctx =
        c.name +
        " — current summary: " +
        c.tagline +
        " Sections: " +
        sections.map((s) => s.h + ": " + s.b).join(" ");
      const result = await api.post<{ reply: string }>("/api/ai/ask", {
        instruction:
          "You are Saga Advisory's analyst. Based on this existing country profile, write a tight 3-sentence 'what changed / what to watch' refresh note for the next regular update. Be concrete and oil & gas / aquaculture commercial in tone. No preamble.\n\n" +
          ctx,
      });
      setDraft((result.reply || "").trim() || "No refresh generated — try again.");
    } catch {
      setDraft("Could not reach Saga AI just now — try again.");
    }
    setLoading(false);
  };

  return (
    <div className="reader-overlay" onMouseDown={close}>
      <div className="reader-panel" onMouseDown={(e) => e.stopPropagation()}>
        <div className="reader-head">
          <span className="reader-flag">{c.flag}</span>
          <div className="reader-titles">
            <h2 className="reader-title">{c.name}</h2>
            <div className="reader-rev">
              {[c.region, c.info_url ? "info.saga-advisory.com" : c.updated_date].filter(Boolean).join(" · ")}
            </div>
          </div>
          <button className="icon-btn" onClick={close}>
            {I.plus({ size: 18, style: { transform: "rotate(45deg)" } })}
          </button>
        </div>

        <div className="reader-body">
          <p className="reader-lede">{c.tagline}</p>
          {c.info_url && (
            <a className="btn ghost sm" href={c.info_url} target="_blank" rel="noreferrer" style={{ textDecoration: "none", marginBottom: 16 }}>
              {I.ext({ size: 13, w: 1.8 })}
              Full profile and latest articles on info.saga-advisory.com
            </a>
          )}

          {sections.map((s, i) => (
            <div key={i} className="reader-section">
              <h3 className="rs-h">{s.h}</h3>
              <p className="rs-b">{s.b}</p>
            </div>
          ))}

          <div className="reader-ai">
            <div className="rai-top">
              <button className="btn primary sm" onClick={refresh} disabled={loading}>
                {I.ai({ size: 15 })}
                {loading ? "Drafting…" : "Draft next update with Saga AI"}
              </button>
            </div>
            {draft && (
              <div className="rai-draft">
                <div className="rai-draft-label">Suggested refresh note</div>
                <p>{draft}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Countries({
  go,
  openAI,
}: {
  go: (screen: string, id?: string) => void;
  openAI: (prefill?: string) => void;
}) {
  const { data: countries } = useData<Country[]>("/api/countries");
  const [openId, setOpenId] = useState<string | null>(null);

  const all = countries || [];
  const live = all.filter((c) => c.overview);
  const todo = all.filter((c) => !c.overview);
  const open = all.find((c) => c.slug === openId) || null;

  return (
    <div className="screen countries-screen">
      <div className="page-head">
        <div>
          <h1 className="page-title">Countries</h1>
          <div className="page-sub">
            Regular updates Saga maintains and sends to partners, operators and governments
          </div>
        </div>
        <div className="cov-pill">
          <span className="live-dot" />
          Drafted by Bj&oslash;rn + Saga AI
        </div>
      </div>

      <div className="country-grid">
        {live.map((c) => (
          <CountryCard key={c.slug} c={c} onOpen={() => setOpenId(c.slug)} />
        ))}
      </div>

      {todo.length > 0 && (
        <div className="country-needs">
          <SectionLabel>Profiles to build</SectionLabel>
          <div className="needs-rows">
            {todo.map((c) => {
              const st = C_STATUS[c.status] || C_STATUS.none;
              return (
                <div key={c.slug} className="need-row">
                  <span className="need-flag">{c.flag}</span>
                  <div className="need-body">
                    <div className="need-name">
                      {c.name}
                      <span className={clsx("c-status", st.cls)}>{st.label}</span>
                    </div>
                    <div className="need-note">{c.tagline}</div>
                  </div>
                  <button
                    className="btn ghost sm"
                    onClick={() => openAI("Draft a country update for " + c.name + ": ")}
                  >
                    {I.ai({ size: 14 })}
                    Draft with AI
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {open && (
        <CountryReader c={open} close={() => setOpenId(null)} go={go} openAI={openAI} />
      )}
    </div>
  );
}

function Principals({
  go,
  partners,
}: {
  go: (screen: string, id?: string) => void;
  partners: Record<string, Partner>;
}) {
  const { data: principalList } = useData<Principal[]>("/api/principals");
  const { data: deals } = useData<Deal[]>("/api/deals");
  const { data: inPartnerList } = useData<InPartner[]>("/api/in-partners");

  const principals = principalList || [];
  const allDeals = deals || [];
  const inPartners = inPartnerList || [];

  return (
    <div className="screen principals">
      <div className="page-head">
        <div>
          <h1 className="page-title">Principals &amp; partners</h1>
          <div className="page-sub">
            The Norwegian companies Saga represents, and the in-country partners who execute
          </div>
        </div>
      </div>

      <SectionLabel>Principals we represent</SectionLabel>
      <div className="prin-grid" style={{ marginTop: 12, marginBottom: 30 }}>
        {principals.map((p) => {
          const pDeals = allDeals.filter((d) => d.principal_id === p.id);
          const val = pDeals.reduce((a, d) => a + (typeof d.value_m === "number" ? d.value_m : 0), 0);
          const hot = pDeals.filter(
            (d) => d.heat === "critical" || d.heat === "high"
          ).length;
          const logo = (p.name || "")
            .replace("Scan Mudring", "Scan")
            .split(" ")
            .map((w) => (w && w[0]) || "")
            .slice(0, 2)
            .join("");
          return (
            <div
              key={p.id}
              className="prin-card clickable"
              onClick={() => go("principal", p.id)}
            >
              <div className="prin-top">
                <span className="prin-logo" style={{ background: p.color }}>
                  {logo}
                </span>
                <span className="prin-sector">{p.sector}</span>
              </div>
              <div className="prin-name">{p.name}</div>
              <div className="prin-base">{p.base}</div>
              <div className="prin-cap">{p.what}</div>
              <div className="prin-foot">
                <div className="prin-stat">
                  <span className="ps-n mono">{pDeals.length}</span>
                  <span className="ps-l">deals</span>
                </div>
                <div className="prin-stat">
                  <span className="ps-n mono">${val.toFixed(1)}M</span>
                  <span className="ps-l">pipeline</span>
                </div>
                {hot > 0 && (
                  <span className="prin-hot">
                    {I.flame({ size: 12, w: 2 })}
                    {hot} hot
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <SectionLabel>In-country partners</SectionLabel>
      <div className="partner-grid" style={{ marginTop: 12 }}>
        {inPartners.map((p) => {
          const pDeals = allDeals.filter((d) => d.in_partner_id === p.id);
          return (
            <div key={p.id} className="partner-card">
              <span className="partner-flag" style={{ background: p.color }}>
                {(p.market || "").slice(0, 2).toUpperCase()}
              </span>
              <div className="partner-body">
                <div className="partner-name">{p.name}</div>
                <div className="partner-role">{p.role}</div>
              </div>
              <span className="partner-deals">
                {pDeals.length} deal{pDeals.length === 1 ? "" : "s"}
              </span>
            </div>
          );
        })}
      </div>
      <div className="partner-note">
        Placeholder names for now &mdash; we'll replace with the real counterparties as
        they're confirmed.
      </div>
    </div>
  );
}

export default function PipelineHub({ go, openAI, initialTab, partners }: PipelineHubProps) {
  const [tab, setTab] = useState(initialTab || "deals");
  useEffect(() => {
    setTab(initialTab || "deals");
  }, [initialTab]);

  const tabs: [string, string][] = [
    ["deals", "Deals"],
    ["countries", "Countries"],
    ["principals", "Principals & partners"],
  ];

  return (
    <ErrorBoundary>
      <div className="screen" style={{ paddingTop: 26 }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
          <div className="seg">
            {tabs.map((t) => (
              <button
                key={t[0]}
                className={clsx("seg-btn", tab === t[0] && "active")}
                onClick={() => setTab(t[0])}
              >
                {t[1]}
              </button>
            ))}
          </div>
        </div>
        {tab === "deals" && <Pipeline go={go} openAI={openAI} partners={partners} />}
        {tab === "countries" && <Countries go={go} openAI={openAI} />}
        {tab === "principals" && <Principals go={go} partners={partners} />}
      </div>
    </ErrorBoundary>
  );
}
